// ========================================
// pages/FAQ.js - Frequently Asked Questions
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Comprehensive FAQ page covering all aspects of the platform
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop: > 1024px (full width)
// - Tablet: 768px - 1024px (adjusted spacing)
// - Mobile: < 768px (stacked layout, larger tap targets)
// 
// SECTIONS:
// - Hero with search bar
// - Expandable FAQ sections (Account, NOKs, External, Trustees, Assets, Pricing, Death Flow, Inheritance, Unclaimed, Security, Technical)
// - General Questions section
// - CTA to contact support
// 
// CSS FIXES (April 12, 2026):
// - Added box-sizing to search bar to prevent horizontal scroll
// - Premium styling for CTA buttons (rounded, proper size)
// - Mobile CTA buttons stack vertically with premium sizing
// ========================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  // ========================================
  // STATE FOR SEARCH AND ACCORDION
  // ========================================
  const [searchTerm, setSearchTerm] = useState('');
  const [openSections, setOpenSections] = useState({});

  // ========================================
  // TOGGLE SECTION ACCORDION
  // ========================================
  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // ========================================
  // FAQ DATA STRUCTURE
  // ========================================
  const faqSections = [
    {
      id: 'account',
      title: '📝 Account & Registration',
      questions: [
        {
          q: "Who can create an Agyapadie Vault account?",
          a: "Any individual aged 18 years or older can create an account. You need a valid email address to register. One email address can only create one account."
        },
        {
          q: "Do I need to verify my email address?",
          a: "Yes. After registration, you will receive a verification email. You must click the verification link to activate your account. This ensures we have a valid contact for you and your beneficiaries."
        },
        {
          q: "What documents do I need to provide?",
          a: "None. Agyapadie Vault values your privacy. You can remain semi-anonymous - we only require your name and email address. No ID verification or document uploads are required for account creation."
        },
        {
          q: "Can I create multiple accounts?",
          a: "No. Each email address can only create one account. One person should maintain one account for all their assets and beneficiaries."
        },
        {
          q: "What is the minimum age requirement?",
          a: "You must be at least 18 years old to register for an Agyapadie Vault account. This is enforced during registration."
        },
        {
          q: "What if I forget my password?",
          a: "Use the 'Forgot Password' link on the login page. You'll receive an email with instructions to reset your password. [Feature coming soon]"
        }
      ]
    },
    {
      id: 'noks',
      title: '👨‍👩‍👧‍👦 Global Next of Kin (NOKs)',
      questions: [
        {
          q: "What are Global Next of Kin?",
          a: "Global Next of Kin (NOKs) are your primary emergency contacts and default beneficiaries. They are created at the account level and automatically linked to EVERY asset you create by default. You can choose to unlink specific NOKs per asset if you add external beneficiaries."
        },
        {
          q: "How many Global NOKs can I have?",
          a: "This depends on your plan: Free (2), Premium (4), Business (6), Enterprise (10)."
        },
        {
          q: "Can a Global NOK also be a Trustee?",
          a: "Yes. It's up to you. The same person can serve as both a Global NOK and a Trustee if you wish."
        },
        {
          q: "Can I delete a Global NOK?",
          a: "Yes, you can delete Global NOKs. However, the system will NOT allow you to delete your last Global NOK - you must always have at least one."
        },
        {
          q: "Can my Global NOKs see my assets while I'm alive?",
          a: "No. Global NOKs cannot see your assets or any details about them while you are alive. They only become relevant when you are deceased."
        },
        {
          q: "Do I need Global NOKs before creating assets?",
          a: "Yes. You must have at least 1 Global NOK before you can create any asset. This ensures your assets always have a default beneficiary."
        }
      ]
    },
    {
      id: 'external',
      title: '👤 External Beneficiaries',
      questions: [
        {
          q: "What are External Beneficiaries?",
          a: "External Beneficiaries are additional beneficiaries you can add PER ASSET. Unlike Global NOKs, they are not shared across all assets. Your plan determines how many you can add per asset."
        },
        {
          q: "How many External Beneficiaries can I add per asset?",
          a: "Premium: 1 per asset, Business: 2 per asset, Enterprise: 3 per asset. Free plan cannot use external beneficiaries."
        },
        {
          q: "Can I delete External Beneficiaries?",
          a: "Yes. Unlike Global NOKs, you can delete all external beneficiaries if you wish. There is no restriction."
        },
        {
          q: "When should I use External vs Global NOKs?",
          a: "Use Global NOKs for people who should inherit from ALL your assets by default. Use External Beneficiaries for specific assets you want to leave to specific people not in your Global NOKs."
        }
      ]
    },
    {
      id: 'trustees',
      title: '⚖️ Trustees',
      questions: [
        {
          q: "What do Trustees do?",
          a: "Trustees are responsible for verifying your death. When you become dormant (not clicking 'I'm Alive' for 90 days), trustees receive emails asking them to confirm whether you are alive or deceased. Their response triggers the inheritance process."
        },
        {
          q: "How many Trustees do I need?",
          a: "Minimum 1 trustee at all times, even if your plan allows more (up to 10 on Enterprise). Trustees must respond to death verification emails."
        },
        {
          q: "What information do I need to add a Trustee?",
          a: "Only their name and email address. No special requirements or verification needed."
        },
        {
          q: "Can a Trustee also be a Global NOK or Beneficiary?",
          a: "Yes. It's up to you. The same person can serve multiple roles in your estate plan."
        },
        {
          q: "What happens if a Trustee doesn't respond to verification emails?",
          a: "Trustees receive monthly emails asking for confirmation. If they remain unresponsive for 1 year (12 monthly emails), they are automatically removed from your trustee list. Both you and the trustee are notified of the removal."
        },
        {
          q: "What happens if ALL Trustees are removed?",
          a: "If you become dormant (90 days without clicking 'I'm Alive') and there are no responsive trustees, assets will be released directly to your beneficiaries."
        },
        {
          q: "Can Trustees see my assets?",
          a: "No. Trustees cannot see your assets or their details. They only act as death verifiers and become primary contacts if assets become unclaimed."
        },
        {
          q: "How does a Trustee accept their role?",
          a: "Trustees receive an email invitation with a unique link. They must click the link and create an account (or log in) to accept the trustee role. Until they accept, they appear as 'pending' in your trustee list."
        }
      ]
    },
    {
      id: 'assets',
      title: '🏦 Assets & Asset Creation',
      questions: [
        {
          q: "What types of assets can I list?",
          a: "You can list bank accounts, properties, vehicles, investments, cryptocurrency wallets, digital assets, insurance policies, will documents, and any other important documents your loved ones need to know about."
        },
        {
          q: "Can I create an asset without any beneficiaries?",
          a: "No. Every asset must have AT LEAST ONE beneficiary (either Global NOK or External Beneficiary). The system will reject asset creation if you uncheck all NOKs and add no external beneficiaries."
        },
        {
          q: "Can I edit an asset after creating it?",
          a: "No. Assets cannot be edited once created. This is by design to maintain the integrity of your asset certificates. Each asset has a unique cryptographic hash and certificate number that is generated at creation time. If you need to make changes, you must delete the asset and create a new one. This ensures the downloaded certificate matches exactly what is displayed in the system."
        },
        {
          q: "What is an Asset Certificate?",
          a: "Every asset you create generates a unique certificate with a hash and certificate number. You can download this certificate as a PDF from your dashboard. The certificate serves as proof of your asset registration."
        },
        {
          q: "What is the asset hash?",
          a: "Each asset has a unique SHA-256 cryptographic hash that serves as a digital fingerprint. You can use this hash to verify that your asset data has not been tampered with."
        }
      ]
    },
    {
      id: 'pricing',
      title: '💰 Plans, Pricing & Upgrades',
      questions: [
        {
          q: "How much do plans cost?",
          a: "All plans are ONE-TIME PAYMENT (lifetime access): Free (GHS 0), Premium (GHS 399), Business (GHS 999), Enterprise (GHS 2,999). No recurring subscriptions or hidden fees."
        },
        {
          q: "How do I upgrade my plan?",
          a: "You purchase a voucher PIN from authorized agents, mobile money vendors (MTN MoMo, Vodafone Cash), or online resellers. Enter the PIN on the Upgrade page to instantly upgrade your plan."
        },
        {
          q: "Can I downgrade my plan?",
          a: "No. Downgrades are not available. You can only upgrade to higher plans. Choose your plan carefully."
        },
        {
          q: "Is there a refund if I change my mind?",
          a: "No. All PIN purchases are final. There are no refunds on one-time payments."
        },
        {
          q: "Where can I buy a PIN?",
          a: "PINs are available from authorized agents nationwide. A list of official vendors will be published soon. You can also contact support for assistance."
        },
        {
          q: "What happens to my plan if I stop using the platform?",
          a: "Your plan remains active forever. Since it's a one-time payment, you keep lifetime access regardless of how often you use the platform."
        }
      ]
    },
    {
      id: 'death-verification',
      title: '💀 Death Verification Flow',
      questions: [
        {
          q: "How does the system know I'm alive?",
          a: "When you log into your dashboard, you'll see an 'I'm Alive' button. You must click this button regularly to remain active in the system."
        },
        {
          q: "What happens if I forget to click 'I'm Alive'?",
          a: "After 90 days of no activity (not clicking 'I'm Alive'), you become 'dormant'. The system immediately begins sending 'Are you alive?' verification emails to your trustees."
        },
        {
          q: "Does normal login reset the 90-day counter?",
          a: "No. Only clicking the specific 'I'm Alive' button on your dashboard resets the 90-day dormant counter."
        },
        {
          q: "What happens when trustees confirm I'm deceased?",
          a: "When trustees confirm your death, your assets become available for your beneficiaries to claim. Beneficiaries receive email notifications to claim their inherited assets."
        },
        {
          q: "What if a trustee confirms I'm alive?",
          a: "Trustees will continue to receive monthly verification emails until YOU log in and click 'I'm Alive'. This ensures your safety - if you're unable to click (e.g., hospitalized), trustees can confirm you're alive."
        },
        {
          q: "What if I'm alive but can't access my account?",
          a: "Trustees can confirm you're alive via the emails they receive. They will continue receiving monthly reminders until you personally click 'I'm Alive'."
        },
        {
          q: "How many trustees need to confirm death?",
          a: "You can choose your verification threshold based on your plan. For example, you can require 2 out of 3 trustees to confirm death before assets are released."
        }
      ]
    },
    {
      id: 'inheritance',
      title: '👨‍👧 Inheritance & Claims',
      questions: [
        {
          q: "How do beneficiaries inherit assets?",
          a: "When your death is verified, beneficiaries receive an email notification. They must create an account (or log in if they already have one) using the same email address that received the notification to claim their inherited assets."
        },
        {
          q: "Is there a time limit to claim assets?",
          a: "No. Assets remain in the system indefinitely until claimed. There is no time limit for beneficiaries to claim their inheritance."
        },
        {
          q: "Can a beneficiary reject their inheritance?",
          a: "Yes. If a beneficiary rejects their inheritance, the asset will be moved to the Unclaimed Assets Registry."
        },
        {
          q: "What if the beneficiary already has an Agyapadie account?",
          a: "They simply log into their existing account. The inherited assets will appear in the 'Inheritance' section of their dashboard."
        },
        {
          q: "Where do I see assets I've inherited?",
          a: "After logging in, inherited assets appear in a special 'Inheritance' section on your dashboard, separate from any assets you may have created yourself."
        },
        {
          q: "Will beneficiaries need to visit a physical office?",
          a: "No. The majority of all dealings are online. Beneficiaries can claim assets and manage inheritance entirely through the web platform. However, our brick and mortar office is always open to the public if needed."
        }
      ]
    },
    {
      id: 'unclaimed',
      title: '📋 Unclaimed Assets Registry',
      questions: [
        {
          q: "What are Unclaimed Assets?",
          a: "Assets that remain unclaimed because: (1) The beneficiary rejected inheritance, (2) The beneficiary never claimed, or (3) Both the user and beneficiary are deceased."
        },
        {
          q: "Are unclaimed assets displayed publicly?",
          a: "Only the NUMBER of unclaimed assets is displayed on our homepage. No asset names, details, or identifying information are shown to the public."
        },
        {
          q: "What happens to unclaimed assets?",
          a: "Our field team will attempt to locate family members of the deceased user and release the assets to them when found. This process involves verification to ensure assets go to the rightful family."
        },
        {
          q: "How long before an asset becomes unclaimed?",
          a: "Assets move to the Unclaimed Assets Registry immediately when: (1) User is verified deceased, and (2) No beneficiary claims the asset, OR (3) Beneficiary rejects inheritance."
        }
      ]
    },
    {
      id: 'security',
      title: '🔒 Security & Privacy',
      questions: [
        {
          q: "How is my data protected?",
          a: "All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Passwords are hashed using bcrypt with per-user salts. Your sensitive information is never stored in plain text."
        },
        {
          q: "Can I verify the integrity of my asset data?",
          a: "Yes. Each asset has a unique hash and certificate number. You can verify your asset's integrity using our internal blockchain explorer - an in-house service that allows you to check that your data hasn't been tampered with."
        },
        {
          q: "What happens if there's a data breach?",
          a: "If a data breach is detected, affected users and beneficiaries will be notified immediately. We commit to notifying within 72 hours as required by GDPR and Data Protection Act 843. Appropriate actions will be taken to secure accounts."
        },
        {
          q: "Is this system legally binding for asset transfer?",
          a: "No. Agyapadie Vault is NOT a legal document system. It is a digital vault for passing vital information to your next generation, dependents, or business replacements. Real assets remain with the users - this system prevents vital details about assets from getting lost when someone dies."
        },
        {
          q: "Do you sell my data to third parties?",
          a: "Never. We do not sell, rent, or trade your personal data. Your information is used only to provide and improve our services."
        }
      ]
    },
    {
      id: 'technical',
      title: '🛠️ Technical & Support',
      questions: [
        {
          q: "What browsers are supported?",
          a: "Agyapadie Vault supports the latest versions of Chrome, Firefox, Safari, and Edge. We recommend using Chrome for the best experience."
        },
        {
          q: "Is there a mobile app?",
          a: "Currently, Agyapadie Vault is a web-based platform optimized for mobile browsers. A native mobile app is planned for future release."
        },
        {
          q: "How do I contact support?",
          a: "You can reach our support team via email at support@agyapadie.com, by phone at +233 (0) 30 212 3456, or through our Contact Us page. Support hours are Monday-Friday, 9am-5pm GMT."
        },
        {
          q: "Do you have a physical office?",
          a: "Yes. Our head office is located at 123 Independence Avenue, Ridge, Accra, Ghana. Appointments are recommended for in-person visits."
        }
      ]
    }
  ];

  // ========================================
  // GENERAL QUESTIONS (Not in sections above)
  // ========================================
  const generalQuestions = [
    {
      q: "What problem does Agyapadie Vault solve?",
      a: "It solves the problem of vital asset information being lost when someone dies. Many people have bank accounts, properties, investments, and digital assets that their families never discover after their passing. Agyapadie Vault ensures your loved ones know about and can access your assets when you're gone."
    },
    {
      q: "Who needs to use this system?",
      a: "Anyone who has assets they want to pass on to others. This includes business owners, property owners, investors, digital asset holders, and anyone who wants to ensure their legacy is preserved and transferred correctly."
    },
    {
      q: "When should I list my assets?",
      a: "As soon as possible. Don't wait. The best time to organize your digital inheritance is now, while you're healthy and can provide complete information."
    }
  ];

  // ========================================
  // FILTER FAQS BASED ON SEARCH
  // ========================================
  const filterFaqs = () => {
    if (!searchTerm.trim()) {
      return { faqSections, generalQuestions };
    }

    const term = searchTerm.toLowerCase();
    
    const filteredSections = faqSections.map(section => ({
      ...section,
      questions: section.questions.filter(q => 
        q.q.toLowerCase().includes(term) || 
        q.a.toLowerCase().includes(term)
      )
    })).filter(section => section.questions.length > 0);

    const filteredGeneral = generalQuestions.filter(q => 
      q.q.toLowerCase().includes(term) || 
      q.a.toLowerCase().includes(term)
    );

    return { faqSections: filteredSections, generalQuestions: filteredGeneral };
  };

  const { faqSections: filteredSections, generalQuestions: filteredGeneral } = filterFaqs();

  return (
    <div className="faq-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO WITH SEARCH */}
      {/* ======================================== */}
      <section className="faq-hero">
        <div className="faq-hero-overlay"></div>
        <div className="faq-container">
          <div className="faq-hero-content">
            <div className="faq-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="faq-badge-icon" />
              <span>Got Questions?</span>
            </div>
            <h1 className="faq-hero-title">
              Frequently Asked <span className="gold">Questions</span>
            </h1>
            <p className="faq-hero-subtitle">
              Everything you need to know about Agyapadie Vault. Can't find what you're looking for? 
              <Link to="/contact" className="faq-contact-link"> Contact our support team</Link>
            </p>
            
            {/* Search Bar */}
            <div className="faq-search">
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="faq-search-input"
              />
              <span className="faq-search-icon">🔍</span>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: FAQ SECTIONS */}
      {/* ======================================== */}
      <section className="faq-sections">
        <div className="faq-container">
          
          {filteredSections.length === 0 && filteredGeneral.length === 0 && (
            <div className="faq-no-results">
              <p>No results found for "{searchTerm}". Please try a different search term.</p>
              <button onClick={() => setSearchTerm('')} className="faq-clear-btn">Clear Search</button>
            </div>
          )}

          {filteredSections.map((section) => (
            <div key={section.id} className="faq-section">
              <button 
                className="faq-section-header"
                onClick={() => toggleSection(section.id)}
              >
                <span className="faq-section-icon">{section.icon || '📌'}</span>
                <h2 className="faq-section-title">{section.title}</h2>
                <span className="faq-section-toggle">{openSections[section.id] ? '−' : '+'}</span>
              </button>
              
              {openSections[section.id] && (
                <div className="faq-section-content">
                  {section.questions.map((item, idx) => (
                    <div key={idx} className="faq-question-card">
                      <div className="faq-question">
                        <span className="faq-q-mark">Q:</span>
                        <strong className="faq-question-text">{item.q}</strong>
                      </div>
                      <div className="faq-answer">
                        <span className="faq-a-mark">A:</span>
                        <span className="faq-answer-text">{item.a}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* General Questions Section */}
          {filteredGeneral.length > 0 && (
            <div className="faq-section">
              <button 
                className="faq-section-header"
                onClick={() => toggleSection('general')}
              >
                <span className="faq-section-icon">❓</span>
                <h2 className="faq-section-title">General Questions</h2>
                <span className="faq-section-toggle">{openSections['general'] ? '−' : '+'}</span>
              </button>
              
              {openSections['general'] && (
                <div className="faq-section-content">
                  {filteredGeneral.map((item, idx) => (
                    <div key={idx} className="faq-question-card">
                      <div className="faq-question">
                        <span className="faq-q-mark">Q:</span>
                        <strong className="faq-question-text">{item.q}</strong>
                      </div>
                      <div className="faq-answer">
                        <span className="faq-a-mark">A:</span>
                        <span className="faq-answer-text">{item.a}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: STILL HAVE QUESTIONS? CTA */}
      {/* ======================================== */}
      <section className="faq-cta">
        <div className="faq-container">
          <div className="faq-cta-card">
            <h2 className="faq-cta-title">Still have questions?</h2>
            <p className="faq-cta-subtitle">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <div className="faq-cta-buttons">
              <Link to="/contact" className="faq-cta-primary">Contact Support →</Link>
              <a href="mailto:support@agyapadie.com" className="faq-cta-secondary">Email Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES - Fully Responsive */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .faq-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .faq-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 0 20px;
          box-sizing: border-box;
        }
        
        .gold {
          color: #FFD700;
        }
        
        /* Hero Section */
        .faq-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .faq-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .faq-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .faq-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .faq-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .faq-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .faq-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .faq-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto 40px;
          line-height: 1.6;
        }
        
        .faq-contact-link {
          color: #FFD700;
          text-decoration: none;
        }
        
        .faq-contact-link:hover {
          text-decoration: underline;
        }
        
        /* Search Bar - Fixed horizontal scroll */
        .faq-search {
          max-width: 500px;
          margin: 0 auto;
          position: relative;
        }
        
        .faq-search-input {
          width: 100%;
          padding: 15px 20px 15px 50px;
          border: none;
          border-radius: 50px;
          font-size: 16px;
          background: white;
          outline: none;
          box-sizing: border-box;
        }
        
        .faq-search-icon {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          opacity: 0.5;
        }
        
        /* FAQ Sections */
        .faq-sections {
          padding: 60px 0;
          background: white;
        }
        
        .faq-section {
          margin-bottom: 20px;
          border: 1px solid #eee;
          border-radius: 12px;
          overflow: hidden;
          background: #F5F0E6;
        }
        
        .faq-section-header {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 25px;
          background: #F5F0E6;
          border: none;
          cursor: pointer;
          text-align: left;
          transition: background 0.3s;
        }
        
        .faq-section-header:hover {
          background: #E8E0D0;
        }
        
        .faq-section-icon {
          font-size: 28px;
          flex-shrink: 0;
        }
        
        .faq-section-title {
          flex: 1;
          font-size: 20px;
          margin: 0;
          color: #0A0A0A;
        }
        
        .faq-section-toggle {
          font-size: 24px;
          color: #FFD700;
          font-weight: bold;
          flex-shrink: 0;
        }
        
        .faq-section-content {
          padding: 20px 25px;
          background: white;
          border-top: 1px solid #eee;
        }
        
        /* Question Cards */
        .faq-question-card {
          margin-bottom: 25px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }
        
        .faq-question-card:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }
        
        .faq-question {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .faq-q-mark {
          font-weight: bold;
          color: #FFD700;
          font-size: 16px;
          flex-shrink: 0;
        }
        
        .faq-question-text {
          font-size: 16px;
          color: #0A0A0A;
          line-height: 1.4;
        }
        
        .faq-answer {
          display: flex;
          gap: 12px;
          padding-left: 28px;
        }
        
        .faq-a-mark {
          font-weight: bold;
          color: #2E7D32;
          font-size: 14px;
          flex-shrink: 0;
        }
        
        .faq-answer-text {
          font-size: 14px;
          color: #555;
          line-height: 1.6;
        }
        
        /* No Results */
        .faq-no-results {
          text-align: center;
          padding: 60px 20px;
          background: #F5F0E6;
          border-radius: 12px;
        }
        
        .faq-no-results p {
          margin-bottom: 20px;
          color: #666;
        }
        
        .faq-clear-btn {
          padding: 10px 24px;
          background: #FFD700;
          border: none;
          border-radius: 5px;
          font-weight: bold;
          cursor: pointer;
        }
        
        /* CTA Section - Premium Buttons */
        .faq-cta {
          padding: 60px 0 80px;
          background: white;
        }
        
        .faq-cta-card {
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          border-radius: 15px;
          padding: 50px;
          text-align: center;
        }
        
        .faq-cta-title {
          font-size: 32px;
          color: white;
          margin-bottom: 15px;
        }
        
        .faq-cta-subtitle {
          font-size: 16px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .faq-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .faq-cta-primary {
          padding: 12px 28px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 40px;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s;
          display: inline-block;
          min-width: 160px;
          text-align: center;
        }
        
        .faq-cta-primary:hover {
          background: #B8860B;
        }
        
        .faq-cta-secondary {
          padding: 12px 28px;
          background: transparent;
          border: 1.5px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 40px;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s;
          display: inline-block;
          min-width: 160px;
          text-align: center;
        }
        
        .faq-cta-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        /* Tablet: 1024px and below */
        @media (max-width: 1024px) {
          .faq-hero-title {
            font-size: 40px;
          }
        }
        
        /* Mobile: 768px and below */
        @media (max-width: 768px) {
          .faq-hero-title {
            font-size: 32px;
          }
          
          .faq-hero-subtitle {
            font-size: 16px;
          }
          
          .faq-section-title {
            font-size: 16px;
          }
          
          .faq-section-icon {
            font-size: 24px;
          }
          
          .faq-section-header {
            padding: 15px 20px;
          }
          
          .faq-section-content {
            padding: 15px 20px;
          }
          
          .faq-question-text {
            font-size: 14px;
          }
          
          .faq-answer-text {
            font-size: 13px;
          }
          
          .faq-cta-title {
            font-size: 24px;
          }
          
          .faq-cta-card {
            padding: 30px 20px;
          }
          
          .faq-cta-buttons {
            flex-direction: column;
            align-items: center;
            gap: 12px;
          }
          
          .faq-cta-primary,
          .faq-cta-secondary {
            width: auto;
            min-width: 200px;
            padding: 10px 24px;
            font-size: 13px;
          }
        }
        
        /* Small Mobile: 480px and below */
        @media (max-width: 480px) {
          .faq-hero-title {
            font-size: 28px;
          }
          
          .faq-search-input {
            font-size: 14px;
            padding: 12px 16px 12px 45px;
          }
          
          .faq-search-icon {
            font-size: 16px;
            left: 15px;
          }
          
          .faq-section-header {
            gap: 10px;
          }
          
          .faq-question {
            flex-direction: column;
            gap: 5px;
          }
          
          .faq-answer {
            padding-left: 0;
          }
          
          .faq-q-mark {
            font-size: 14px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default FAQ;